import { currentDateString } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import getEnvironment from 'app/environment'
import { t } from 'app/i18l'
import * as Calendar from 'expo-calendar'
import * as Linking from 'expo-linking'
import moment from 'moment'
import React, { createContext, useCallback, useContext, useEffect } from 'react'
import { Platform } from 'react-native'
import { useSchedule } from 'schedule/queries/list'
import { confirmationAlert } from 'shared/alerts'
import { useStoredState } from 'shared/hooks/stored_state'
import { generateUniqueId } from 'shared/utils'
import { addEventsToCalendar } from './util'

export type CalendarSyncContextValue = {
  calendarSyncSettingValue: boolean | undefined
  setCalendarSyncSettingValue: React.Dispatch<
    React.SetStateAction<boolean | undefined>
  >
  initiateCalendarSync: () => Promise<void>
  hideCalendarSyncModal: boolean | undefined
  setHideCalendarSyncModal: React.Dispatch<
    React.SetStateAction<boolean | undefined>
  >
}

export const CalendarSyncContext = createContext({} as CalendarSyncContextValue)

interface Props {
  children: React.ReactNode
}

const NUMBER_OF_WEEKS_DURATION = 2

export function CalendarSyncProvider(props: Props) {
  const [calendarSyncSettingValue, setCalendarSyncSettingValue] =
    useStoredState<boolean>('calendar_sync_setting_value')

  const [shiftsCalendarId, setShiftsCalendarId] = useStoredState<string | null>(
    'shifts_calendar_id'
  )

  const [hideCalendarSyncModal, setHideCalendarSyncModal] =
    useStoredState<boolean>('show_calendar_sync_modal')

  const { theme } = useTheme()

  useEffect(() => {
    if (calendarSyncSettingValue === null) {
      setCalendarSyncSettingValue(false)
    }
  }, [calendarSyncSettingValue, setCalendarSyncSettingValue])

  const startDateString = currentDateString()
  const endDateString = moment(startDateString)
    .add(NUMBER_OF_WEEKS_DURATION, 'weeks')
    .format('YYYY-MM-DD')

  const scheduleQuery = useSchedule(
    {
      schedule: { calendar: 'helper' },
      startDate: startDateString,
      endDate: endDateString,
    },
    generateUniqueId
  )

  const envName = getEnvironment().name

  const createCalendar = useCallback(async () => {
    const defaultCalendarSource =
      Platform.OS === 'ios'
        ? await getDefaultCalendarSource()
        : {
            isLocalAccount: true,
            name: 'Default',
            type: Calendar.SourceType.LOCAL,
          }

    const newCalendarId = await Calendar.createCalendarAsync({
      name: `NEXT Calendar - ${envName}`,
      title: `${t('myShifts')}${
        envName !== 'production' ? ` - ${envName}` : ''
      }`,
      color: theme.colors?.primary,
      entityType: Calendar.EntityTypes.EVENT,
      source: defaultCalendarSource,
      sourceId: defaultCalendarSource.id,
      allowsModifications: true,
      ownerAccount: 'personal',
      allowedReminders: [
        Calendar.AlarmMethod.ALARM,
        Calendar.AlarmMethod.ALERT,
        Calendar.AlarmMethod.SMS,
      ],
      isSynced: true,
      accessLevel: Calendar.CalendarAccessLevel.OWNER,
    })

    return newCalendarId
  }, [envName, theme.colors?.primary])

  const syncShiftsToCalendar = useCallback(async () => {
    const startDate = new Date()
    const endDate = moment(startDate)
      .add(NUMBER_OF_WEEKS_DURATION, 'weeks')
      .toDate()

    const existingEvents = shiftsCalendarId
      ? await Calendar.getEventsAsync([shiftsCalendarId], startDate, endDate)
      : undefined

    const scheduleEvents = scheduleQuery.data?.events
    if (scheduleEvents && shiftsCalendarId) {
      const eventIds = await addEventsToCalendar(
        scheduleEvents,
        shiftsCalendarId,
        existingEvents
      )
    }
  }, [scheduleQuery.data?.events, shiftsCalendarId])

  const removeCalendar = useCallback(async () => {
    if (shiftsCalendarId) {
      await Calendar.deleteCalendarAsync(shiftsCalendarId)
      setShiftsCalendarId(null)
    }
  }, [setShiftsCalendarId, shiftsCalendarId])

  const initiateCalendarSync = useCallback(async () => {
    if (calendarSyncSettingValue) {
      try {
        const { status } = await Calendar.requestCalendarPermissionsAsync()
        if (status === 'granted') {
          try {
            const calendars = await Calendar.getCalendarsAsync(
              Calendar.EntityTypes.EVENT
            )
            if (calendars && calendars.length > 0) {
              if (
                shiftsCalendarId === null ||
                (shiftsCalendarId !== undefined &&
                  !calendars.find(calendar => calendar.id === shiftsCalendarId))
              ) {
                try {
                  const id = await createCalendar()
                  if (id) {
                    setShiftsCalendarId(id)
                  }
                } catch (error) {
                  console.log('Error creating calendar ', error)
                }
              } else if (
                shiftsCalendarId &&
                !!calendars.find(calendar => calendar.id === shiftsCalendarId)
              ) {
                try {
                  await syncShiftsToCalendar()
                } catch (error) {
                  console.log('Error syncing shifts ', error)
                }
              }
            }
          } catch (error) {
            console.log('Error getting all calendars ', error)
          }
        } else if (status === 'denied') {
          const confirmed = await confirmationAlert(
            t('calendarPermissionDeniedTitle'),
            t('calendarPermissionDeniedBody')
          )
          if (confirmed) {
            await Linking.openSettings()
          } else {
            setCalendarSyncSettingValue(false)
          }
        }
      } catch (error) {
        console.log('Error requesting calendar permission. ', error)
      }
    } else if (
      calendarSyncSettingValue === false &&
      shiftsCalendarId !== null
    ) {
      await removeCalendar()
    }
  }, [
    calendarSyncSettingValue,
    createCalendar,
    removeCalendar,
    setCalendarSyncSettingValue,
    setShiftsCalendarId,
    shiftsCalendarId,
    syncShiftsToCalendar,
  ])

  useEffect(() => {
    initiateCalendarSync().catch(e => null)
  }, [initiateCalendarSync])

  const value: CalendarSyncContextValue = {
    calendarSyncSettingValue,
    setCalendarSyncSettingValue,
    initiateCalendarSync,
    hideCalendarSyncModal,
    setHideCalendarSyncModal,
  }

  return (
    <CalendarSyncContext.Provider value={value}>
      {props.children}
    </CalendarSyncContext.Provider>
  )
}

export function useCalendarSyncContext() {
  return useContext<CalendarSyncContextValue>(CalendarSyncContext)
}

async function getDefaultCalendarSource(): Promise<Calendar.Source> {
  const defaultCalendar = await Calendar.getDefaultCalendarAsync()

  const sources = await Calendar.getSourcesAsync()

  const iCloudSource = sources.find(
    source =>
      source.type === Calendar.SourceType.CALDAV && source.name === 'iCloud'
  )

  return iCloudSource ? iCloudSource : defaultCalendar.source
}
